import Err from '@leverege/error'
import FieldFactory from './FieldFactory.js'
import Util from './Util.js'

const openers = /^[({'"[]$/
const closers = /^[)}'"\]]$/
const closeMatches = {
  '(' : ')',
  '{' : '}',
  '\'' : '\'',
  '"' : '"',
  '[' : ']',
}

const indexOfOver = ( val, { field, context } ) => {
  const stack = []
  let token = ''
  for ( let i = 0; i < val.length; i++ ) {
    const char = val[i]
    if ( char.match( openers ) ) {
      stack.push( char )
    }
    if ( char.match( closers ) ) {
      if ( closeMatches[stack.at( -1 )] !== char ) {
        return Util.handleInvalid(
          field,
          Err.illegalArgument( { message : 'bad string: unmatched parentheses', index : i, field : val } ),
          context
        )
      }
      stack.pop()
    }
    if ( char.trim() === '' && stack.length === 0 ) {
      if ( token.trim().toLowerCase() === 'over' ) {
        return i
      }
      token = ''
    } else {
      token += char
    }
  }
  return -1
}

export default {
  id : 'Expression.FieldParser.WindowField',
  type : 'windowField',
  sort : -9,
  handles : ( field, context ) => {
    return field.type === 'ExpressionParser.Field' && indexOfOver( field.value, { context, field } ) !== -1
  },
  parse : async ( field, context ) => {
    const hasAlias = Util.handleAlias( field.value, { context : { ...context, field } } )
    let value = field.value, alias
    if ( hasAlias ) {
      value = hasAlias.field
      alias = hasAlias.alias
    }
    const idx = indexOfOver( value, { context, field } )
    if ( idx?.type === 'ExpressionField.invalid' ) {
      return idx
    }
    const pre = value.slice( 0, idx - 4 ).trim()
    const preRemoved = value.slice( 0, idx - 4 ).indexOf( pre )
    const over = value.slice( idx ).trim()
    const overRemoved = value.slice( idx ).indexOf( over )
    const overStartIndex = idx + overRemoved

    const childContext = {
      ...context,
      parent : { type : 'ExpressionField.window' },
      field,
    }
    const partitioned = Util.findRootToken(
      over,
      {
        token : 'partition',
        context : childContext
      }
    )
    let ordered, orderFields, partitionFields
    if ( !partitioned ) {
      ordered = Util.findRootToken(
        over,
        { token : 'order', context : childContext }
      )
      if ( !ordered ) {
        return Util.handleInvalid(
          field,
          Err.illegalArgument( { message : 'invalid window function, order or partition is required', field, context } ),
          context
        )
      }
      orderFields = await FieldFactory.create(
        ordered.post,
        { ...childContext, indexOffset : overStartIndex + ordered.relativeLocation.postIndex }
      )
    } else {
      ordered = Util.findRootToken(
        partitioned.post,
        { token : 'order', context : childContext }
      )
      if ( !ordered ) {
        partitionFields = await FieldFactory.create(
          partitioned.post,
          {
            ...childContext,
            indexOffset : overStartIndex + partitioned.relativeLocation.postIndex }
        )
      } else {
        partitionFields = await FieldFactory.create( ordered.pre, {
          ...childContext,
          indexOffset : overStartIndex + partitioned.relativeLocation.postIndex
        } )
        orderFields = await FieldFactory.create(
          ordered.post,
          {
            ...childContext,
            indexOffset : overStartIndex + ordered.relativeLocation.postIndex + partitioned.relativeLocation.postIndex
          }
        )
      }
    }
    const windowField = await FieldFactory.create( pre, {
      ...childContext,
      indexOffset : preRemoved
    } )
    const toReturn = {
      type : 'ExpressionField.window',
      field : windowField.fields[0],
      over : {
        order : orderFields?.fields,
        partition : partitionFields?.fields,
      },
    }
    if ( hasAlias ) {
      toReturn.alias = alias
    }
    if ( context?.includeAstInfo ) {
      toReturn.absoluteLocation = {
        startIndex : field.startIndex,
        endIndex : field.endIndex,
      }
      toReturn.relativeLocation = {
        startIndex : 0,
        endIndex : field.untrimmed?.length
      }
      toReturn.untrimmed = field.untrimmed
      if ( hasAlias ) {
        toReturn.aliasLocation = {
          absolute : hasAlias.absoluteLocation,
          relative : hasAlias.relativeLocation,
        }
      }
    }
    return toReturn
  }
}
